import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Merge rapid filter changes into one section request.
   * This prevents several near-simultaneous requests when customers tap
   * multiple filter values quickly.
   */
  #scheduleSectionUpdate = debounce(() => {
    this.#updateSection();
  }, 80);

  #pendingUpdateBehavior = {
    closeDrawer: false,
    feedback: 'selection',
    message: 'Refined',
  };

  /**
   * Toggle a quick list filter such as In stock.
   * @param {Event} event
   */
  applyQuickListFilter(event) {
    const button = event.target instanceof HTMLElement ? event.target : null;
    if (!button) return;

    const name = button.dataset.filterName;
    const value = button.dataset.filterValue;
    if (!name || value == null) return;

    const input = Array.from(this.refs.facetsForm.elements).find((element) => {
      return element instanceof HTMLInputElement && element.name === name && element.value === value;
    });

    if (!(input instanceof HTMLInputElement) || input.disabled) return;

    input.checked = !input.checked;
    button.setAttribute('aria-pressed', input.checked ? 'true' : 'false');
    button.classList.toggle('is-active', input.checked);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Apply one of the quick price presets. Presets deliberately close the
   * drawer after the refreshed products arrive.
   * @param {Event} event
   */
  applyQuickPrice(event) {
    const button = event.target instanceof HTMLElement ? event.target : null;
    if (!button) return;

    const priceComponent = this.querySelector('price-facet-component');
    if (!(priceComponent instanceof PriceFacetComponent)) return;

    const isActive = button.getAttribute('aria-pressed') === 'true';
    priceComponent.applyQuickPriceValues(
      isActive ? '' : button.dataset.priceMin ?? '',
      isActive ? '' : button.dataset.priceMax ?? '',
      isActive ? 'Price cleared' : 'Price refined'
    );
  }


  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = (options = {}) => {
    this.#pendingUpdateBehavior = {
      closeDrawer: options.closeDrawer === true,
      feedback: options.feedback ?? 'selection',
      message: options.message ?? 'Refined',
    };

    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#setLoadingState(true);
    this.#scheduleSectionUpdate();
  };

  /**
   * Updates the section
   */
  async #updateSection() {
    const requestURL = new URL(window.location.href);

    try {
      // Render the exact current URL. The SectionRenderer already prevents an
      // older request from morphing over a newer request.
      await sectionRenderer.renderSection(this.sectionId, { url: requestURL });

      this.#setLoadingState(false);

      const behavior = this.#pendingUpdateBehavior;
      this.#pendingUpdateBehavior = {
        closeDrawer: false,
        feedback: 'selection',
        message: 'Refined',
      };

      if (behavior.closeDrawer) {
        const mobileDrawerWasOpen = this.#closeAppliedFilterUI();
        const confirmApplied = () => {
          if (behavior.feedback === 'applied') this.#showFilterAppliedHint(behavior.message);
          this.#giveSuccessHaptic(16);
        };

        if (mobileDrawerWasOpen) {
          window.setTimeout(confirmApplied, 170);
        } else {
          confirmApplied();
        }
      } else if (behavior.feedback === 'selection') {
        // Tiny tactile confirmation without interrupting multi-filter shopping.
        this.#giveSuccessHaptic(7);
      }
    } catch (error) {
      this.#setLoadingState(false);
      console.error('Unable to update filtered products.', error);
    }
  }

  /**
   * Closes the mobile filter drawer and any open desktop filter dropdown
   * after a successful filter refresh.
   */
  #closeAppliedFilterUI() {
    // Mobile drawer: use the theme's own close button first so its scroll-lock
    // and dialog state are cleaned up correctly.
    const drawer = document.querySelector('#filters-drawer dialog[open]');
    if (drawer instanceof HTMLDialogElement) {
      const closeButton = drawer.querySelector('.facets-drawer__close');

      if (closeButton instanceof HTMLButtonElement) {
        closeButton.click();
      } else {
        drawer.close();
      }
    }

    // Desktop horizontal/vertical filter details.
    document.querySelectorAll('.facets__panel[open]').forEach((panel) => {
      if (panel instanceof HTMLDetailsElement) panel.open = false;
    });

    return drawer instanceof HTMLDialogElement;
  }

  /**
   * Small non-blocking confirmation shown after the results are actually updated.
   */
  #showFilterAppliedHint(message = 'Refined') {
    const existing = document.getElementById('sv-filter-applied-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.id = 'sv-filter-applied-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = `<span aria-hidden="true">✓</span><span>${message}</span>`;

    Object.assign(toast.style, {
      position: 'fixed',
      left: '50%',
      bottom: 'calc(18px + env(safe-area-inset-bottom))',
      zIndex: '2147483646',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '7px',
      maxWidth: 'calc(100vw - 32px)',
      padding: '9px 13px',
      border: '1px solid #dedcd5',
      borderRadius: '999px',
      background: '#fbfaf6',
      color: '#27342a',
      boxShadow: '0 12px 28px rgba(39, 52, 42, 0.12)',
      fontFamily: 'var(--font-paragraph--family)',
      fontSize: '11px',
      fontWeight: '600',
      lineHeight: '1',
      letterSpacing: '0.01em',
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      opacity: '0',
      transform: 'translate3d(-50%, 8px, 0) scale(0.98)',
      transition: 'opacity 170ms cubic-bezier(0.33,1,0.68,1), transform 210ms cubic-bezier(0.22,1,0.36,1)',
      willChange: 'opacity, transform',
    });

    const check = toast.firstElementChild;
    if (check instanceof HTMLElement) {
      Object.assign(check.style, {
        display: 'grid',
        width: '18px',
        height: '18px',
        placeItems: 'center',
        borderRadius: '50%',
        background: '#f4f6f2',
        color: '#657a46',
        fontSize: '10px',
        fontWeight: '700',
      });
    }

    document.body.appendChild(toast);

    requestAnimationFrame(() => {
      toast.style.opacity = '1';
      toast.style.transform = 'translate3d(-50%, 0, 0) scale(1)';
    });

    window.setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translate3d(-50%, 5px, 0) scale(0.985)';

      window.setTimeout(() => {
        if (toast.isConnected) toast.remove();
      }, 220);
    }, 1150);
  }

  /**
   * Short success vibration on devices/browsers that support the Vibration API.
   * Unsupported devices simply ignore it.
   */
  #giveSuccessHaptic(duration = 10) {
    if (typeof navigator.vibrate === 'function') {
      navigator.vibrate(duration);
    }
  }

  /**
   * Gives immediate visual feedback while products are refreshing.
   * The class disappears automatically when the section morphs successfully.
   * @param {boolean} isLoading
   */
  #setLoadingState(isLoading) {
    const section = this.closest('.shopify-section');
    if (!(section instanceof HTMLElement)) return;

    section.classList.toggle('sv-facets-updating', isLoading);

    if (isLoading) {
      section.setAttribute('aria-busy', 'true');
    } else {
      section.removeAttribute('aria-busy');
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url, options = {}) {
    this.#pendingUpdateBehavior = {
      closeDrawer: options.closeDrawer === true,
      feedback: options.feedback ?? 'selection',
      message: options.message ?? 'Refined',
    };

    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#setLoadingState(true);
    this.#scheduleSectionUpdate.cancel();
    this.#scheduleSectionUpdate();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters({ closeDrawer: false, feedback: 'selection' });
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
    details?.classList.toggle('sv-refine-active', checkedInputElements.length > 0);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {string} */
  currency;
  /** @type {string} */
  moneyFormat;

  /**
   * Live price typing waits briefly so entering 1500 doesn't submit 1, 15,
   * 150, and 1500 as four separate requests.
   */
  schedulePriceFilterAndResults = debounce(() => {
    // Kept for compatibility with older markup. The Refine experience waits
    // for explicit Apply/Enter so typing never causes a surprise refresh.
  }, 650);

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.currency = this.dataset.currency ?? 'USD';
    this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat ?? '{{amount}}');
    this.dataset.dirty = 'false';
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    this.schedulePriceFilterAndResults.cancel();
  }

  /**
   * Extracts the placeholder from a money format string, removing currency symbols.
   * @param {string} format
   * @returns {string}
   */
  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  /**
   * Handles keyboard input. Enter applies the range immediately.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.schedulePriceFilterAndResults.cancel();
      this.applyPriceFilter();
      return;
    }

    if (event.metaKey || event.ctrlKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Mark manual price entry as edited. No network request is made until Apply
   * or Enter, which keeps typing smooth and predictable.
   */
  handlePriceInput() {
    this.dataset.dirty = 'true';
    this.#updateSummary();
  }

  /**
   * Kept for compatibility with older markup.
   */
  updatePriceFilterAndResults() {
    this.#commitPrice({ closeDrawer: false, feedback: 'none' });
  }

  /**
   * Normalizes values, validates the range, then updates products.
   */
  applyPriceFilter() {
    this.#commitPrice({ closeDrawer: true, feedback: 'applied', message: 'Price applied' });
  }

  /**
   * Called by the Quick refine presets in the drawer header.
   * @param {string} min
   * @param {string} max
   */
  applyQuickPriceValues(min, max, message = 'Price refined') {
    const { minInput, maxInput } = this.refs;
    minInput.value = min;
    maxInput.value = max;
    this.dataset.dirty = 'true';
    this.#updateSummary();
    this.#commitPrice({ closeDrawer: true, feedback: 'applied', message });
  }

  #commitPrice({ closeDrawer, feedback, message }) {
    const { minInput, maxInput } = this.refs;

    this.#normalizeInput(minInput);
    this.#normalizeInput(maxInput);
    this.#normalizeRangeOrder();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    this.schedulePriceFilterAndResults.cancel();
    facetsForm.updateFilters({ closeDrawer, feedback, message });
    this.#updateSummary();

    const details = this.closest('details');
    details?.classList.toggle('sv-refine-active', Boolean(minInput.value || maxInput.value));
  }

  /**
   * Parses user-entered money into minor units.
   * @param {string} displayValue
   * @returns {number}
   */
  #parseDisplayValue(displayValue) {
    return convertMoneyToMinorUnits(displayValue, this.currency) ?? 0;
  }

  /**
   * Currency decimal precision for converting minor units back to a URL-safe
   * major-unit value such as 1500 or 1500.5 (never 1,500.00).
   */
  #currencyPrecision() {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.currency,
      }).resolvedOptions().maximumFractionDigits;
    } catch (error) {
      return 2;
    }
  }

  /**
   * @param {number} minorUnits
   * @returns {string}
   */
  #minorUnitsToURLValue(minorUnits) {
    const precision = this.#currencyPrecision();
    const divisor = Math.pow(10, precision);
    let value = (minorUnits / divisor).toFixed(precision);

    if (precision > 0) {
      value = value.replace(/\.?0+$/, '');
    }

    return value;
  }

  /**
   * Accept user-friendly input such as 1500, 1,500, or 1500.50, but always
   * convert it to Shopify's clean numeric URL format before submitting.
   * @param {HTMLInputElement} input
   */
  #normalizeInput(input) {
    const rawValue = input.value.trim();
    if (rawValue === '') return;

    const value = this.#parseDisplayValue(rawValue);
    const minimum = this.#parseDisplayValue(input.getAttribute('data-min') ?? '0');

    // Do not clamp to the collection's current highest price. Customers can
    // intentionally enter a higher minimum (which should return no products)
    // or a higher maximum (which simply includes every cheaper product).
    input.value = this.#minorUnitsToURLValue(Math.max(minimum, value));
  }

  /**
   * If the customer enters the endpoints backwards, make a valid range rather
   * than returning an apparently broken empty grid.
   */
  #normalizeRangeOrder() {
    const { minInput, maxInput } = this.refs;
    if (!minInput.value || !maxInput.value) return;

    const min = this.#parseDisplayValue(minInput.value);
    const max = this.#parseDisplayValue(maxInput.value);

    if (min <= max) return;

    minInput.value = this.#minorUnitsToURLValue(max);
    maxInput.value = this.#minorUnitsToURLValue(min);
  }

  /**
   * Updates the price summary shown in the filter heading.
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters({ closeDrawer: false, feedback: 'selection' });
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url, { closeDrawer: false, feedback: 'selection' });
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const activeClass = this.getAttribute('active-class') || 'active';
      clearButton.classList.toggle(activeClass, event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters({ closeDrawer: false, feedback: 'selection' });
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement) && !(event.target instanceof HTMLInputElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    const optionName = event.target.dataset.optionName ?? '';
    facetStatus.textContent = optionName;
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;
    const refineSummary = this.dataset.refineSummary === 'true';

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (refineSummary) {
      const labels = checkedInputElements
        .map((input) => input.dataset.label ?? input.getAttribute('aria-label') ?? '')
        .filter(Boolean);

      facetStatus.textContent =
        checkedInputElementsCount <= 2 ? labels.join(', ') : `${checkedInputElementsCount} selected`;
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const currency = facetStatus.dataset.currency || '';
    const minInputNum = this.#parseCents(minInputValue, '0', currency);
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax, currency);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as minor units (cents for most currencies, but adjusted for zero-decimal currencies)
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid (formatted string like "11,400")
   * @param {string} currency - The currency code (e.g., 'USD', 'JPY', 'KRW')
   * @returns {number} The money value in minor units
   */
  #parseCents(value, fallback = '0', currency = '') {
    // Try to parse the value
    const result = convertMoneyToMinorUnits(value, currency);
    if (result !== null) return result;

    // Fall back to parsing the fallback string (which may have formatting like "11,400")
    const fallbackResult = convertMoneyToMinorUnits(fallback, currency);
    if (fallbackResult !== null) return fallbackResult;

    // Last resort: clean and parse as integer
    const cleanFallback = fallback.replace(/[^\d]/g, '');
    return parseInt(cleanFallback, 10) || 0;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const format = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return formatMoney(moneyValue, format, currency);
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}
